import GradientColorPicker, {
  DEFAULT_GRADIENT_VALUE,
  parseValues,
} from 'lib/ui/form/BackgroundPicker/GradientColorPicker'
import styled from 'styled-components'
import ImagePicker from 'lib/ui/form/BackgroundPicker/ImagePicker'
import SolidColorPicker from 'lib/ui/form/BackgroundPicker/SolidColorPicker'
import React, {useState} from 'react'
import Tabs from '@material-ui/core/Tabs'
import Tab from '@material-ui/core/Tab'
import TabPanel from 'lib/ui/tabs/TabPanel'
import {hex} from 'lib/color'

export type BackgroundPickerProps = {
  label: string
  background?: string
  onChange: (background: string) => void
  disabled?: boolean
  hasFreeSize?: boolean
  className?: string
}

const SOLID = 0
const GRADIENT = 1
const IMAGE = 2

type BackgroundType = typeof SOLID | typeof GRADIENT | typeof IMAGE

export default function BackgroundPicker(props: BackgroundPickerProps) {
  const {background, onChange} = props

  // The showing tab derives from the background value; the user's picked
  // tab only decides when the value is empty and could belong to any tab.
  const [selectedType, setSelectedType] = useState<BackgroundType | null>(null)

  const [prevSolidBackground, setPrevSolidBackground] = useState<
    string | undefined
  >(undefined)
  const [prevGradientBackground, setPrevGradientBackground] = useState<
    string | undefined
  >(undefined)
  const [prevImageBackground, setPrevImageBackground] = useState<
    string | undefined
  >(undefined)

  const type = typeFromBackground(background) ?? selectedType ?? SOLID

  const handleTypeChange = (
    _: React.ChangeEvent<{}>,
    nextType: BackgroundType,
  ) => {
    if (nextType === type) {
      return
    }

    // Stash the value under the tab we're leaving so switching back
    // restores it.
    if (type === SOLID) {
      setPrevSolidBackground(background)
    }
    if (type === GRADIENT) {
      setPrevGradientBackground(background)
    }
    if (type === IMAGE) {
      setPrevImageBackground(background)
    }

    setSelectedType(nextType)

    onChange(
      nextBackground({
        fromType: type,
        nextType,
        background,
        prevSolidBackground,
        prevGradientBackground,
        prevImageBackground,
      }),
    )
  }

  return (
    <Box className={props.className}>
      <StyledTabs
        value={type}
        onChange={handleTypeChange}
        TabIndicatorProps={{
          style: {
            display: 'none',
          },
        }}
        scrollButtons="off"
      >
        <StyledTab
          aria-label="set solid background"
          label="Solid"
          disableRipple
          value={SOLID}
        />
        <StyledTab
          aria-label="set gradient background"
          label="Gradient"
          disableRipple
          value={GRADIENT}
        />
        <StyledTab
          aria-label="set image background"
          label="Image"
          disableRipple
          value={IMAGE}
        />
      </StyledTabs>

      <StyledTabPanel currentIndex={type} index={SOLID}>
        <SolidColorPicker {...props} />
      </StyledTabPanel>

      <StyledTabPanel currentIndex={type} index={GRADIENT}>
        <GradientColorPicker {...props} />
      </StyledTabPanel>

      <StyledTabPanel currentIndex={type} index={IMAGE}>
        <ImagePicker {...props} />
      </StyledTabPanel>
    </Box>
  )
}

function typeFromBackground(background?: string): BackgroundType | null {
  if (background?.startsWith('url')) {
    return IMAGE
  }

  if (background?.startsWith('linear-gradient')) {
    return GRADIENT
  }

  if (background) {
    return SOLID
  }

  return null
}

// Seeds the value for the tab being switched to: solid<->gradient carry the
// picked color across, everything else restores the stashed value for the
// destination tab.
function nextBackground(params: {
  fromType: BackgroundType
  nextType: BackgroundType
  background: string | undefined
  prevSolidBackground: string | undefined
  prevGradientBackground: string | undefined
  prevImageBackground: string | undefined
}): string {
  const {
    fromType,
    nextType,
    background,
    prevSolidBackground,
    prevGradientBackground,
    prevImageBackground,
  } = params

  if (nextType === GRADIENT && fromType === SOLID) {
    return gradientFromSolid(background, prevGradientBackground)
  }

  if (nextType === SOLID && fromType === GRADIENT) {
    return parseValues(background).color1
  }

  if (nextType === GRADIENT) {
    return prevGradientBackground ?? ''
  }

  if (nextType === SOLID) {
    return prevSolidBackground ?? ''
  }

  if (nextType === IMAGE) {
    return prevImageBackground ?? ''
  }

  return ''
}

function gradientFromSolid(
  currentBackground: string | undefined,
  prevGradientBackground: string | undefined,
) {
  const color1 = hex(currentBackground) || '#FFFFFF'

  if (prevGradientBackground == null) {
    const {degrees, color2} = DEFAULT_GRADIENT_VALUE
    return `linear-gradient(${degrees}deg,${color1},${color2})`
  }

  const {degrees, color2} = parseValues(prevGradientBackground)
  return `linear-gradient(${degrees}deg,${color1},${color2})`
}

// We're going to export the container here in case any parent needs to override
// the style via CSS. ie., instead a config panel where we would like to give
// the input negative margins.
export const Box = styled.div``

const StyledTabs = styled(Tabs)`
  background: ${(props) => props.theme.colors.input.background};
  padding: 2px;
  min-height: auto !important;

  // Container
  > div > div {
    justify-content: space-between;
    padding: 0 ${(props) => props.theme.spacing[4]};
  }
`

const StyledTab = styled(Tab)`
  background-color: ${(props) => props.theme.colors.input.dark};
  margin: 0px 1px !important;
  max-width: 100%;
  min-width: auto !important;
  min-height: auto !important;
  border-radius: 4px;
  font-style: normal;
  font-weight: normal;
  font-size: 14px !important;
  line-height: 18px !important;
  padding: ${(props) =>
    `${props.theme.spacing[2]} ${props.theme.spacing[4]}`} !important;

  &.Mui-selected {
    background-color: ${(props) => props.theme.colors.panel.background};
    border-radius: 4px;
  }
`

const StyledTabPanel = styled(TabPanel)`
  padding: ${(props) => `${props.theme.spacing[6]} ${props.theme.spacing[5]}`} 0 !important;
`
